import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Download, Upload, FileJson, Sheet, Users, Video, GraduationCap, Award, AlertTriangle,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function downloadBlob(path, fallbackName) {
  // Uses fetch with credentials so httpOnly cookies flow through
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const dispo = res.headers.get("Content-Disposition") || "";
  const m = dispo.match(/filename="?([^"]+)"?/i);
  const filename = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

const CSV_EXPORTS = [
  { path: "/admin/export/courses.csv", label: "Courses", icon: FileJson, hint: "All courses with counts, publish flag" },
  { path: "/admin/export/lessons.csv", label: "Lessons & YouTube links", icon: Video, hint: "Every lesson, video_id and youtu.be URL" },
  { path: "/admin/export/students.csv", label: "Students", icon: Users, hint: "Registered students & points" },
  { path: "/admin/export/enrollments.csv", label: "Enrollments & progress", icon: GraduationCap, hint: "Per-student progress %" },
  { path: "/admin/export/certificates.csv", label: "Certificates", icon: Award, hint: "Issued certificates with scores" },
];

export default function AdminBackup() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [mode, setMode] = useState("merge");
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);

  async function fullBackup() {
    setDownloading(true);
    try {
      await downloadBlob("/admin/backup/full", "infiniteskills-backup.json");
      toast.success("Backup downloaded");
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(false); }
  }

  async function csvExport(path, label) {
    try {
      await downloadBlob(path, `${label.toLowerCase().replace(/[^a-z]+/g, "-")}.csv`);
      toast.success(`${label} exported`);
    } catch (e) { toast.error(e.message); }
  }

  async function restore() {
    if (!file) return toast.error("Choose a backup JSON file first");
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post("/admin/backup/restore", fd);
      const counts = Object.entries(data.result || {})
        .map(([k, v]) => `${k}: ${Object.values(v).reduce((a, b) => a + b, 0)}`)
        .join(" · ");
      toast.success(`Restore complete (${mode}) — ${counts}`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRestoring(false); }
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-backup">
      {/* Full backup */}
      <div className="rounded-3xl bg-card border border-border p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-2xl royal-gradient text-white flex items-center justify-center">
            <FileJson className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <h3 className="font-display text-xl font-bold">Full JSON backup</h3>
            <p className="text-sm text-muted-foreground mt-1">
              One-click download of everything: courses, modules, lessons, YouTube video IDs,
              users, enrollments, quiz results, certificates, assignments, announcements & live sessions.
              Keep it safe — you can restore later from this file.
            </p>
          </div>
          <Button onClick={fullBackup} disabled={downloading} data-testid="backup-download-btn"
            className="rounded-full royal-gradient border-0 text-white btn-lift">
            <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Preparing..." : "Download backup"}
          </Button>
        </div>
      </div>

      {/* Restore */}
      <div className="rounded-3xl bg-card border border-border p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl gold-gradient text-[#1E3A8A] flex items-center justify-center">
            <Upload className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold">Restore from backup</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Upload a previously downloaded <code>.json</code> backup to import data.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4">
          <div className="rounded-2xl border border-border p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Restore mode</p>
            <RadioGroup value={mode} onValueChange={setMode} className="grid sm:grid-cols-2 gap-3">
              <label className={`rounded-2xl border p-3.5 cursor-pointer transition ${mode === "merge" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="merge" id="mode-merge" data-testid="restore-mode-merge" />
                  <Label htmlFor="mode-merge" className="cursor-pointer font-semibold">Merge (safe)</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Update existing records by ID and insert new ones. Non-destructive.</p>
              </label>
              <label className={`rounded-2xl border p-3.5 cursor-pointer transition ${mode === "replace" ? "border-destructive bg-destructive/5" : "border-border hover:border-destructive/40"}`}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="replace" id="mode-replace" data-testid="restore-mode-replace" />
                  <Label htmlFor="mode-replace" className="cursor-pointer font-semibold text-destructive">Replace (destructive)</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Wipe each collection and restore only what's in the backup file.</p>
              </label>
            </RadioGroup>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <input ref={inputRef} type="file" accept="application/json,.json"
              data-testid="restore-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-secondary file:px-4 file:py-2 file:font-medium file:cursor-pointer" />
            {file && <span className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</span>}
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={!file || restoring} data-testid="restore-btn"
                className={`self-start rounded-full border-0 text-white btn-lift ${mode === "replace" ? "bg-destructive hover:bg-destructive/90" : "royal-gradient"}`}>
                <Upload className="h-4 w-4 mr-1.5" /> {restoring ? "Restoring..." : `Restore (${mode})`}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" /> Confirm restore
                </AlertDialogTitle>
                <AlertDialogDescription>
                  You are about to restore in <b>{mode.toUpperCase()}</b> mode from <b>{file?.name}</b>.
                  {mode === "replace" && <> This will <b className="text-destructive">wipe all existing courses, students, enrollments and other records</b> before importing. This cannot be undone.</>}
                  {mode === "merge" && <> Existing records with matching IDs will be updated; new ones will be added.</>}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={restore} data-testid="restore-confirm-btn"
                  className={mode === "replace" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}>
                  Yes, restore
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* CSV / Excel exports */}
      <div className="rounded-3xl bg-card border border-border p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <Sheet className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold">Export to Excel / CSV</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Download individual reports in <code>.csv</code>. Opens directly in Excel, Google Sheets or Numbers.
            </p>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {CSV_EXPORTS.map((x) => (
            <button key={x.path} onClick={() => csvExport(x.path, x.label)}
              data-testid={`csv-${x.label.split(" ")[0].toLowerCase()}`}
              className="text-left rounded-2xl border border-border hover:border-primary/40 hover:bg-secondary/40 transition p-4 flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                <x.icon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{x.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{x.hint}</p>
              </div>
              <Download className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
